import React, {useEffect, useMemo, useRef, useState} from "react";
import {Template} from "../../../../components/template/template.jsx";
import {Card} from "../../../../components/card/card.jsx";
import Button from "../../../../components/button/button.jsx";
import InputDatetime from "../../../../components/input/input-datetime.jsx";
import InputTextarea from "../../../../components/input/input-textarea.jsx";
import InputCheckbox from "../../../../components/input/input-checkbox.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import SentryRow from "../../../../components/template/sentry-row.jsx";
import {useLocation, useParams, useSearchParams} from "react-router";
import {FaCar, FaPlus, FaPrint, FaSyncAlt, FaWalking} from "react-icons/fa";
import {useQuery} from "@tanstack/react-query";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import {openDispatchWeb} from "../../utils/dispatch-list-utils.js";
import projectUtils, {clearRequiredFieldError} from "../../../../utils/project-utils.js";
import GarrisonService from "../../../../services/dispatch/garrison-service.js";
import GarrisonCompositionList from "./components/garrison-composition-list.jsx";
import GarrisonAnimalList from "./components/garrison-animal-list.jsx";
import GarrisonDispatchTable from "./components/garrison-dispatch-table.jsx";
import GarrisonPanicTable from "./components/garrison-panic-table.jsx";
import GarrisonFixedLocationInspection from "./components/garrison-fixed-location-inspection.jsx";
import GarrisonScheduleList from "../../utils/components/garrison-schedule-list.jsx";
import GarrisonScheduleModal from "./components/garrison-schedule-modal.jsx";
import {
    INTENDED_FUNCTION_FIXED_LOCATION,
    buildFixedLocationInspection,
    buildGarrisonModel,
    isIntegrantRequired,
    mapAnimalOptions,
    mapContingentOptions,
    mapIntendedFunctions,
    mapLeaders,
    mapPlaces,
    mapSectors,
    mapTeams,
} from "./utils/garrison-utils.js";
import GarrisonMaterialCautionTable from "./components/garrison-material-caution-table.jsx";
import {finishDashboardEdit} from "../../../../utils/dashboard-edit-window.js";

const API_DESPACHO_WEB = import.meta.env.VITE_API_DESPACHO_WEB;
const REQUIRED_BG = "!bg-[#fdfdee]";
const REQUIRED_LABEl_BG = "!bg-[linear-gradient(to_bottom,transparent_50%,#fdfdee_50%)]";

const initialState = {
    id: undefined,
    systemUpdate: "",
    start: "",
    end: "",
    finalized: false,
    leaderId: "",
    team: "",
    intendedFunction: "",
    place: "",
    placeId: null,
    sectors: [],
    initialObservation: "",
    finalObservation: "",
    schedulesJustification: "",
};

const hasLateActivity = (schedules = []) =>
    schedules.some(schedule => (schedule.activities ?? []).some(activity => (activity.dispatchList ?? []).length === 0));

const GarrisonForm = () => {
    const navigate = navigateHook();
    const {id} = useParams();
    const {pathname} = useLocation();
    const [searchParams] = useSearchParams();

    const copyId = searchParams.get("cp");
    const redir = searchParams.get("redir");
    const retroactive = pathname.includes("retroactive");
    const mode = copyId ? "copy" : (id ? "edit" : "create");

    const [state, setState] = useState(initialState);
    const [meta, setMeta] = useState({boName: "", myGarrisonPermission: false, isSuggestKm: false, hasFixedLocationItems: false});
    const [options, setOptions] = useState({leaders: [], teams: [], intendedFunctions: [], places: [], sectors: []});

    const [availableCompositions, setAvailableCompositions] = useState([]);
    const [contingentOptions, setContingentOptions] = useState([]);
    const [animalOptions, setAnimalOptions] = useState([]);

    const [compositionRows, setCompositionRows] = useState([]);
    const [animalRows, setAnimalRows] = useState([]);

    const [materialCaution, setMaterialCaution] = useState([]);
    const [dispatchRows, setDispatchRows] = useState([]);
    const [panicRows, setPanicRows] = useState([]);

    const [loadedSchedules, setLoadedSchedules] = useState([]);
    const [addedSchedules, setAddedSchedules] = useState([]);
    const [justifySchedules, setJustifySchedules] = useState(false);

    const [existingInspection, setExistingInspection] = useState(null);
    const [inspectionRows, setInspectionRows] = useState([]);

    const [initiallyFinalized, setInitiallyFinalized] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showAddMenu, setShowAddMenu] = useState(false);
    const [showScheduleModal, setShowScheduleModal] = useState(false);

    const tempKey = useRef(0);
    const addMenuRef = useRef(null);
    const compositionRequiredRef = useRef(null);
    const nextTempId = () => `temp-${tempKey.current++}`;

    useEffect(() => {
        if (compositionRows.length > 0) clearRequiredFieldError(compositionRequiredRef.current);
    }, [compositionRows.length]);

    useEffect(() => {
        if (!showAddMenu) return;

        const closeMenu = (event) => {
            if (!addMenuRef.current?.contains(event.target)) setShowAddMenu(false);
        };

        document.addEventListener("mousedown", closeMenu);
        return () => document.removeEventListener("mousedown", closeMenu);
    }, [showAddMenu]);

    const withVehicleOptions = useMemo(() => availableCompositions
        .filter(item => item.type === "WITH_VEHICLE")
        .map(item => ({
            value: item.id,
            label: item.vehicleModel?.name,
            vehicleId: item.vehicleModel?.id,
            kmControl: !!item.vehicleModel?.kmControl,
            actualKm: item.vehicleModel?.actualKm,
            maintenanceExpired: item.maintenanceExpired ?? null,
            positions: item.positions ?? [],
        })), [availableCompositions]);

    const withoutVehicleOptions = useMemo(() => availableCompositions
        .filter(item => item.type === "WITHOUT_VEHICLE")
        .map(item => ({
            value: item.id,
            label: item.name,
            vehicleId: undefined,
            kmControl: false,
            positions: item.positions ?? [],
        })), [availableCompositions]);

    const buildIntegrants = (positions = [], saved = null) => positions.map((position, index) => {
        const savedIntegrant = saved?.integrants?.[index] ?? saved?.integrants?.[position.name];
        return {
            position: position.name,
            conductor: !!position.conductor,
            display: !!position.display,
            mandatory: position.mandatory !== undefined ? !!position.mandatory : !!savedIntegrant?.mandatory,
            contingentId: savedIntegrant?.contingent?.id ?? savedIntegrant?.contingentId ?? "",
            integrantId: savedIntegrant?.id ?? undefined,
            cnhWarning: null,
            cnhWarningText: "",
        };
    });

    const buildEditCompositionRows = (compositions = []) => compositions.map(item => ({
        tempId: nextTempId(),
        disabled: true,
        hasVehicle: item.type === "WITH_VEHICLE",
        compositionId: item.id,
        vehicleId: item.vehicleId ?? item.vehicleModel?.id,
        kmControl: !!item.vehicleModel?.kmControl,
        initialMileage: item.initialMileage ?? "",
        finalMileage: item.finalMileage ?? "",
        maintenanceExpired: null,
        displayName: item.type === "WITHOUT_VEHICLE" ? item.name : item.vehicleModel?.name,
        integrants: (item.integrants ?? []).map(integrant => ({
            position: integrant.position,
            conductor: !!integrant.conductor,
            display: !!integrant.display,
            mandatory: !!integrant.mandatory,
            contingentId: integrant.contingent?.id ?? "",
            integrantId: integrant.id,
            cnhWarning: null,
            cnhWarningText: "",
        })),
    }));

    const buildCopyCompositionRows = (compositions = [], available = []) => compositions.map(item => {
        const hasVehicle = item.type === "WITH_VEHICLE";
        const compositionId = item.compositionId ?? item.id;
        const source = available.find(composition => String(composition.id) === String(compositionId));
        const positions = source?.positions ?? (item.integrants ?? []).map(integrant => ({name: integrant.position, conductor: false}));
        return {
            tempId: nextTempId(),
            disabled: false,
            hasVehicle,
            compositionId,
            vehicleId: source?.vehicleModel?.id ?? item.vehicleId,
            kmControl: source?.vehicleModel?.kmControl != null ? !!source.vehicleModel.kmControl : !!item.vehicleModel?.kmControl,
            initialMileage: item.initialMileage ?? "",
            finalMileage: item.finalMileage ?? "",
            maintenanceExpired: source?.maintenanceExpired ?? null,
            integrants: buildIntegrants(positions, item),
        };
    });

    const {data, isLoading, refetch} = useQuery({
        queryKey: ["dispatch-garrison-form", mode, id, copyId, retroactive],
        queryFn: async () => {
            const data = mode === "edit"
                ? await GarrisonService.formData(id, retroactive).catch(() => null)
                : await GarrisonService.formData(null, retroactive);

            if (mode === "edit" && !data) {
                navigate("/404");
                return;
            }

            setMeta({
                boName: data.boName ?? "",
                myGarrisonPermission: !!data.myGarrisonPermission,
                isSuggestKm: !!data.isSuggestKm,
                hasFixedLocationItems: data.allFixedLocationItems != null,
            });

            setMaterialCaution(data.materialCaution ?? []);
            setDispatchRows(data.dispatchGarrison ?? []);
            setPanicRows(data.panics ?? []);

            const compositionData = mode === "copy"
                ? await GarrisonService.copy(copyId)
                : await GarrisonService.compositionList(mode === "edit" ? id : "");

            setAvailableCompositions(compositionData.compositions ?? []);

            const formGarrison = mode === "edit" ? data.garrison : compositionData.garrison;

            const integrantContingents = (formGarrison?.composition ?? [])
                .flatMap(composition => composition.integrants ?? [])
                .map(integrant => integrant.contingent)
                .filter(contingent => contingent && contingent.id != null);

            const contingentList = [...(compositionData.contingent ?? []), ...integrantContingents];

            setContingentOptions(
                mapContingentOptions(contingentList)
                    .filter((option, index, list) => list.findIndex(item => String(item.value) === String(option.value)) === index)
            );

            const garrisonAnimals = formGarrison?.animalModel ?? [];
            const animalSource = [...(compositionData.animals ?? []), ...garrisonAnimals];
            const animalOptionsList = mapAnimalOptions(animalSource)
                .filter((option, index, list) => list.findIndex(item => String(item.value) === String(option.value)) === index);

            setAnimalOptions(animalOptionsList);

            const ensureOption = (list, value, makeOption) =>
                (value && !list.some(option => String(option.value) === String(value))) ? [...list, makeOption(value)] : list;

            const teamOptions = ensureOption(mapTeams(data.teams), formGarrison?.team, value => ({value, label: value}));
            const placeOptions = ensureOption(mapPlaces(data.placesModel), formGarrison?.place, value => ({value, label: value, placeId: null}));
            const sectorOptions = (formGarrison?.sectors ?? []).reduce(
                (list, value) => ensureOption(list, value, sector => ({value: sector, label: sector})),
                mapSectors(data.sectors));

            setOptions({
                leaders: mapLeaders(data.leaders),
                teams: teamOptions,
                intendedFunctions: mapIntendedFunctions(data.intendedFunctions),
                places: placeOptions,
                sectors: sectorOptions,
            });

            const activeInspectionItems = (data.allFixedLocationItems ?? []).filter(item => item.active);

            if (mode === "edit") {
                const garrison = data.garrison ?? {};
                const finalized = !!garrison.finalized;

                setState({
                    ...initialState,
                    id,
                    systemUpdate: garrison.systemUpdate ?? "",
                    start: garrison.start ?? "",
                    end: garrison.end ?? "",
                    finalized,
                    leaderId: garrison.leaderId ?? "",
                    team: garrison.team ?? "",
                    intendedFunction: garrison.intendedFunction ?? "",
                    place: garrison.place ?? "",
                    placeId: (data.placesModel ?? []).find(place => place.name === garrison.place)?.id ?? null,
                    sectors: garrison.sectors ?? [],
                    initialObservation: garrison.initialObservation ?? "",
                    finalObservation: garrison.finalObservation ?? "",
                    schedulesJustification: garrison.schedulesJustification ?? "",
                });

                setInitiallyFinalized(finalized);
                setCompositionRows(buildEditCompositionRows(garrison.composition, compositionData.compositions));
                setAnimalRows((garrison.animalModel ?? []).map(animal => ({tempId: nextTempId(), animalId: animal.id})));

                const existing = data.fixedLocationInspection?.data ?? null;
                setExistingInspection(existing);
                if (existing) {
                    setInspectionRows(existing.map(item => ({item: item.item, answer: item.answer ?? "", disabled: !!item.answer})));
                } else {
                    setInspectionRows(activeInspectionItems.map(item => ({item: item.item, answer: "", disabled: false})));
                }

                const schedules = await GarrisonService.getGarrisonSchedule(id).catch(() => []);
                setLoadedSchedules((schedules ?? []).map(schedule => ({...schedule, removable: false})));
                setJustifySchedules(hasLateActivity(schedules ?? []));
            } else if (mode === "copy") {
                const garrison = compositionData.garrison ?? {};
                setState({
                    ...initialState,
                    start: data.date ?? "",
                    end: data.date ?? "",
                    leaderId: garrison.leaderId ?? "",
                    team: garrison.team ?? "",
                    intendedFunction: garrison.intendedFunction ?? "",
                    place: garrison.place ?? "",
                    placeId: (data.placesModel ?? []).find(place => place.name === garrison.place)?.id ?? null,
                    sectors: garrison.sectors ?? [],
                    initialObservation: garrison.initialObservation ?? "",
                });
                setCompositionRows(buildCopyCompositionRows(garrison.composition ?? [], compositionData.compositions ?? []));
                setAnimalRows((garrison.animalModel ?? []).map(animal => ({tempId: nextTempId(), animalId: animal.id})));
                setExistingInspection(null);
                setInspectionRows(activeInspectionItems.map(item => ({item: item.item, answer: "", disabled: false})));
            } else {
                setState({
                    ...initialState,
                    start: retroactive ? "" : (data.date ?? ""),
                    end: retroactive ? "" : (data.date ?? ""),
                    finalized: retroactive,
                });
                setInitiallyFinalized(false);
                setCompositionRows([]);
                setAnimalRows([]);
                setLoadedSchedules([]);
                setAddedSchedules([]);
                setJustifySchedules(false);
                setShowAddMenu(false);
                setExistingInspection(null);
                setInspectionRows(activeInspectionItems.map(item => ({item: item.item, answer: "", disabled: false})));
            }

            return data;
        },
    });

    const inspectionVisible = meta.hasFixedLocationItems && !!state.place && state.intendedFunction === INTENDED_FUNCTION_FIXED_LOCATION;
    const renderInspectionCard = !(state.finalized && !existingInspection);
    const editDisabled = mode === "edit";
    const compositionEditable = mode !== "edit";
    const showFinalizeCheckbox = mode === "edit" || retroactive;
    const finalizeDisabled = retroactive || initiallyFinalized;
    const showEnd = state.finalized || retroactive;
    const showFinalObservation = state.finalized;
    const showSaveCopy = state.finalized && !retroactive;
    const showMaterialCaution = materialCaution.length > 0;
    const showDispatchCard = dispatchRows.length > 0 || (meta.myGarrisonPermission && mode === "edit");
    const showDispatchActions = meta.myGarrisonPermission && mode === "edit";
    const showScheduleCard = !retroactive && !(state.finalized && loadedSchedules.length === 0 && addedSchedules.length === 0);
    const allSchedules = [...loadedSchedules, ...addedSchedules];

    const findOption = (list, value) => list.find(option => String(option.value) === String(value)) ?? null;

    const loadInspectionForPlace = async (placeId) => {
        if (existingInspection || !placeId) return;
        try {
            const result = await GarrisonService.fixedLocationByLastPlace(placeId);
            const answers = result?.data ?? [];
            setInspectionRows(prev => prev.map((row, index) => ({...row, answer: answers[index]?.answer ?? ""})));
        } catch (error) {
            console.error(error);
        }
    };

    const onChangePlace = (option) => {
        setState(prev => ({...prev, place: option?.value ?? "", placeId: option?.placeId ?? null}));
        if (meta.hasFixedLocationItems && option?.value && state.intendedFunction === INTENDED_FUNCTION_FIXED_LOCATION) {
            loadInspectionForPlace(option?.placeId);
        }
    };

    const onChangeIntendedFunction = (option) => {
        const intendedFunction = option?.value ?? "";
        setState(prev => ({...prev, intendedFunction}));
        if (meta.hasFixedLocationItems && state.place && intendedFunction === INTENDED_FUNCTION_FIXED_LOCATION) {
            loadInspectionForPlace(state.placeId);
        }
        if (mode === "create") reloadSchedulesToAdd({...state, intendedFunction});
    };

    const reloadSchedulesToAdd = async (nextState) => {
        if (retroactive || state.finalized) return;

        const groups = JSON.stringify({
            intendendFunction: nextState.intendedFunction || "",
            team: nextState.team || "",
            sectors: nextState.sectors ?? [],
        });
        try {
            const schedules = await GarrisonService.getListSchedules(null, groups);
            const autoApply = (schedules ?? [])
                .filter(schedule => schedule.autoApplyByGroup)
                .map(schedule => ({...schedule, removable: true}));
            setAddedSchedules(autoApply);
        } catch (error) {
            console.error(error);
        }
    };

    const addCompositionRow = (hasVehicle) => {
        const last = compositionRows[compositionRows.length - 1];
        if (last && !last.compositionId) {
            projectUtils.swalErrorMessage("Campos obrigatórios", "Informe ao menos uma composição para adicionar outra.", "Ok");
            return;
        }

        setShowAddMenu(false);
        setCompositionRows(prev => [...prev, {
            tempId: nextTempId(), disabled: false, hasVehicle, compositionId: "", vehicleId: "",
            kmControl: false, initialMileage: "", finalMileage: "", maintenanceExpired: null, integrants: [],
        }]);
    };

    const onSelectComposition = (tempId, option) => {
        setCompositionRows(prev => prev.map(row => {
            if (row.tempId !== tempId) return row;
            if (!option) return {...row, compositionId: "", vehicleId: "", kmControl: false, initialMileage: "", finalMileage: "", maintenanceExpired: null, integrants: []};
            const initialMileage = row.hasVehicle && option.kmControl && option.actualKm ? String(option.actualKm) : "";
            return {
                ...row,
                compositionId: option.value,
                vehicleId: option.vehicleId,
                kmControl: !!option.kmControl,
                maintenanceExpired: option.maintenanceExpired ?? null,
                initialMileage,
                finalMileage: "",
                integrants: buildIntegrants(option.positions ?? []),
            };
        }));
    };

    const onChangeKm = (tempId, field, value) =>
        setCompositionRows(prev => prev.map(row => row.tempId === tempId ? {...row, [field]: value} : row));

    const onChangeIntegrant = (tempId, index, option) =>
        setCompositionRows(prev => prev.map(row => {
            if (row.tempId !== tempId) return row;
            const integrants = row.integrants.map((integrant, position) => position === index
                ? {
                    ...integrant,
                    contingentId: option?.value ?? "",
                    integrantId: option ? integrant.integrantId : undefined,
                    cnhWarning: option?.cnhWarning ?? null,
                    cnhWarningText: option?.cnhWarningText ?? "",
                }
                : integrant);
            return {...row, integrants};
        }));

    const removeCompositionRow = (tempId) =>
        setCompositionRows(prev => prev.filter(row => row.tempId !== tempId));

    const addAnimalRow = () => {
        setAnimalRows(prev => [...prev, {tempId: nextTempId(), animalId: ""}]);
    };

    const changeAnimal = (tempId, value) =>
        setAnimalRows(prev => prev.map(row => row.tempId === tempId ? {...row, animalId: value} : row));

    const removeAnimal = (tempId) =>
        setAnimalRows(prev => prev.filter(row => row.tempId !== tempId));

    const onChangeAnswer = (index, value) =>
        setInspectionRows(prev => prev.map((row, position) => position === index ? {...row, answer: value} : row));

    const onToggleFinalize = async (checked) => {
        if (!checked) {
            setState(prev => ({...prev, finalized: false}));
            return;
        }

        let end = state.end;
        try {
            end = await GarrisonService.getDateFormattedView();
        } catch (error) {
            console.error(error);
        }

        setState(prev => ({...prev, finalized: true, end: end ?? prev.end}));

        if (meta.isSuggestKm) {
            setCompositionRows(prev => prev.map(row => {
                const composition = availableCompositions.find(item => String(item.id) === String(row.compositionId));
                const actualKm = composition?.vehicle?.actualKm ?? composition?.vehicleModel?.actualKm;
                if (actualKm == null) return row;
                return {...row, finalMileage: String(actualKm)};
            }));
        }
    };

    const addDispatch = () => {
        GarrisonService.getDispatchStatus(id)
            .then(hasOpen => {
                if (hasOpen) {
                    projectUtils.swalErrorMessage("Erro", "Essa guarnição já possui um despacho aberto", "Ok");
                    return;
                }
                openDispatchWeb(navigate, `/dispatch/form?my-garrison=${id}`);
            })
            .catch(error => {
                console.error(error);
                projectUtils.swalErrorMessage("Erro", "Erro ao abrir despacho", "Ok");
            });
    };

    const refreshDispatch = () => {
        GarrisonService.getDispatchHistoryByGarrison(id)
            .then(rows => setDispatchRows(rows ?? []))
            .catch(error => {
                console.error(error);
                projectUtils.swalErrorMessage("", "Não foi possível atualizar os Despachos / Atividades.", "Ok");
            });
    };

    const onConfirmSchedules = (selected) => {
        setAddedSchedules(prev => {
            const existing = new Set([...prev, ...loadedSchedules].map(schedule => schedule.id));
            const additions = selected.filter(schedule => !existing.has(schedule.id)).map(schedule => ({...schedule, removable: true}));
            return [...prev, ...additions];
        });
    };

    const removeSchedule = (scheduleId) =>
        setAddedSchedules(prev => prev.filter(schedule => schedule.id !== scheduleId));

    const hasValue = (value) => value !== null && value !== undefined && String(value).trim() !== "";

    const collectMissing = () => {
        const missing = [];
        if (!hasValue(state.start)) missing.push("Data/Hora inicial");
        if (showEnd && !hasValue(state.end)) missing.push("Data/Hora final");
        if (!hasValue(state.leaderId)) missing.push("Responsável");
        if (!compositionRows.length) missing.push("Composição");
        compositionRows.forEach(row => {
            if (!hasValue(row.compositionId)) {
                missing.push("Composição");
                return;
            }

            row.integrants
                .filter(isIntegrantRequired)
                .forEach(integrant => {
                    if (!hasValue(integrant.contingentId)) {
                        missing.push(integrant.conductor ? "Condutor" : integrant.display ? "Encarregado" : "Integrante");
                    }
                });
            if (row.kmControl && !hasValue(row.initialMileage)) missing.push("Km inicial");
        });
        if (justifySchedules && state.finalized && !hasValue(state.schedulesJustification)) {
            missing.push("Justificativa de atividades não realizadas");
        }
        return [...new Set(missing)];
    };

    const persist = ({addAfter = false, addCopy = false} = {}) => {
        if (inspectionVisible && inspectionRows.some(row => !row.answer)) {
            projectUtils.swalErrorMessage("", "O campo \"Situação\" é obrigatório.", "Ok");
            return;
        }

        const missing = collectMissing();
        if (missing.length) {
            projectUtils.swalErrorMessage("", "Preencha os campos obrigatórios", "Ok");
            return;
        }

        const animals = animalRows.map(row => row.animalId).filter(Boolean);
        const scheduleIds = [...new Set(allSchedules.map(schedule => schedule.id))];

        const model = buildGarrisonModel({
            state,
            retroactive,
            mode,
            compositionRows,
            animals,
            scheduleIds,
            actions: {addNew: mode === "edit", addNewHome: addAfter, addNewAndCopy: addCopy},
        });

        const inspectionJson = JSON.stringify(buildFixedLocationInspection(inspectionRows));

        if (retroactive) {
            submit(model, inspectionJson, {addAfter, addCopy});
            return;
        }

        GarrisonService.verifyMileageBeforeUpsert(model)
            .then(response => {
                submit(model, inspectionJson, {addAfter, addCopy});

            })
            .catch((response) => {

                if (response.response?.status !== 406) {
                    projectUtils.swalErrorMessage("Erro", "Erro na verificação do km", "Ok")
                    return;
                }

                const errorMessage = response.response?.data;
                const message = typeof errorMessage === "string" && errorMessage ? errorMessage
                    : "A quilometragem informada é menor que a anterior. Deseja continuar?";

                projectUtils.swalMessage("warning", "", message, "Sim", true, "Não", ({isConfirmed}) => {
                    if (isConfirmed) submit(model, inspectionJson, {addAfter, addCopy});
                }, false, "warning", true);

            });
    };

    const printGarrison = () => navigate(`/reports/garrison_info?id=${id}`, true);

    const submit = (model, inspectionJson, {addAfter, addCopy}) => {
        setIsSaving(true);

        const request = mode === "edit"
            ? GarrisonService.update(id, model, inspectionJson)
            : GarrisonService.save(model, inspectionJson);

        request
            .then((data) => {
                if (addAfter && mode === "create") {
                    projectUtils.success(false);
                    refetch();
                    return;
                }

                if (addAfter) {
                    const url = redir ? `?redir=${redir}` : "";
                    projectUtils.success(true);
                    navigate(`${API_DESPACHO_WEB}/garrison/form${url}`, false);
                    return;
                }

                if (addCopy) {
                    const savedId = data?.id ?? state.id ?? id;
                    const redirUrl = redir ? `&redir=${redir}` : "";
                    projectUtils.success(true);
                    navigate(`${API_DESPACHO_WEB}/garrison/form?cp=${savedId}${redirUrl}`, false);
                    return;
                }

                projectUtils.success(true);
                if (redir === "despacho/dashboard") {
                    if (mode === "edit" && finishDashboardEdit(redir, navigate, "Guarnição salva com sucesso.")) return;
                    navigate(`/${redir}`, false);
                    return;
                }

                const target = redir ? `/${redir}` : API_DESPACHO_WEB + "/garrison";
                navigate(target, false);
            })
            .catch(async (error) => {
                console.error(error);
                let message = "Não foi possível salvar.";
                if (error.response?.status !== 500) {
                    message = error.response?.data?.errorMessages[0] ?? message;
                }

                const htmlRegex = /<[a-z][\s\S]*>/i;
                const useHtml = htmlRegex.test(message)
                projectUtils.swalErrorMessage("", message, "Ok", false, "Não", undefined, useHtml);
            })
            .finally(() => setIsSaving(false));

    };

    const newGarrison = () => navigate(API_DESPACHO_WEB + "/garrison/form", false);

    const groups = JSON.stringify({
        intendendFunction: state.intendedFunction || "",
        team: state.team || "",
        sectors: state.sectors ?? [],
    });

    return (
        <Template isLoading={isLoading || isSaving}>
            <Template.Header>
                <Template.Title browserTitle={'Guarnição'}>Guarnição {mode === "edit" ? <span className="text-[12px]">(ID: {id})</span> : ""}</Template.Title>
                {mode === "edit" &&
                    <div className="flex gap-2">
                        <Button title="Imprimir" target="primary" onClick={printGarrison}><FaPrint/></Button>
                        <Button target="primary-outline" onClick={newGarrison}><FaPlus/>Adicionar guarnição</Button>
                    </div>
                }
            </Template.Header>
            <Template.Body className="flex flex-col space-y-2">
                {retroactive &&
                    <div className="rounded-md bg-red-100 text-red-700 px-4 py-2 text-sm">
                        O cadastro retroativo deve ser utilizado apenas para cadastrar guarnições já encerradas!
                    </div>
                }

                <Card>
                    <Card.Header>
                        <Card.Title>Dados</Card.Title>
                    </Card.Header>
                    <Card.Body className="flex flex-col gap-4">
                        <SentryRow className="items-center">
                            <InputDatetime
                                classNameWrapper={"col-span-3 " + REQUIRED_BG}
                                labelClass={REQUIRED_LABEl_BG}
                                id="start"
                                label="Data/Hora inicial *"
                                dateFormat="LL"
                                returnDateObj={false}
                                selected={state.start}
                                onChange={(value) => setState(prev => ({...prev, start: value ?? ""}))}
                            />
                            {showEnd &&
                                <InputDatetime
                                    classNameWrapper={"col-span-3 " + REQUIRED_BG}
                                    labelClass={REQUIRED_LABEl_BG}
                                    id="end"
                                    label="Data/Hora final *"
                                    dateFormat="LL"
                                    returnDateObj={false}
                                    selected={state.end}
                                    onChange={(value) => setState(prev => ({...prev, end: value ?? ""}))}
                                />
                            }
                            {showFinalizeCheckbox &&
                                <div className="col-span-3 col-start-10 flex justify-end">
                                    <InputCheckbox
                                        id="finalized"
                                        label="Encerrar guarnição"
                                        classNameLabel="font-bold text-sm"
                                        checked={state.finalized}
                                        disabled={finalizeDisabled}
                                        onChange={({target}) => onToggleFinalize(target.checked)}
                                    />
                                </div>
                            }
                        </SentryRow>

                        <hr className="border-[#ededed]"/>

                        <SentryRow>
                            <SelectComponent
                                classNameWrapper={"col-span-4 " + REQUIRED_BG}
                                labelClass={REQUIRED_LABEl_BG}
                                label="Responsável *"
                                options={options.leaders}
                                isDisabled={editDisabled}
                                value={findOption(options.leaders, state.leaderId)}
                                onChange={(option) => setState(prev => ({...prev, leaderId: option?.value ?? ""}))}
                            />
                            <SelectComponent
                                classNameWrapper="col-span-4"
                                label="Equipe"
                                options={options.teams}
                                isDisabled={editDisabled && !!data?.garrison?.team}
                                value={findOption(options.teams, state.team)}
                                onChange={(option) => {
                                    const team = option?.value ?? "";
                                    setState(prev => ({...prev, team}));
                                    if (mode === "create") reloadSchedulesToAdd({...state, team});
                                }}
                            />
                            <SelectComponent
                                classNameWrapper="col-span-4"
                                label="Função destinada"
                                options={options.intendedFunctions}
                                isDisabled={editDisabled && !!data?.garrison?.intendedFunction}
                                value={findOption(options.intendedFunctions, state.intendedFunction)}
                                onChange={onChangeIntendedFunction}
                            />
                            <SelectComponent
                                classNameWrapper="col-span-4"
                                label="Local pré-cadastrado"
                                options={options.places}
                                isDisabled={editDisabled && !!data?.garrison?.place}
                                value={findOption(options.places, state.place)}
                                onChange={onChangePlace}
                            />
                            <SelectComponent
                                classNameWrapper="col-span-8"
                                label="Setor"
                                isMulti
                                options={options.sectors}
                                isDisabled={editDisabled && data?.garrison?.sectors?.length > 0}
                                value={options.sectors.filter(option => state.sectors.includes(option.value))}
                                onChange={(selected) => {
                                    const sectors = (selected ?? []).map(option => option.value);
                                    setState(prev => ({...prev, sectors}));
                                    if (mode === "create") reloadSchedulesToAdd({...state, sectors});
                                }}
                            />
                        </SentryRow>
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Header>
                        <Card.Title>Composições *</Card.Title>
                    </Card.Header>
                    <Card.Body className="flex flex-col gap-3">
                        <GarrisonCompositionList
                            rows={compositionRows}
                            withVehicleOptions={withVehicleOptions}
                            withoutVehicleOptions={withoutVehicleOptions}
                            contingentOptions={contingentOptions}
                            onSelectComposition={onSelectComposition}
                            onChangeKm={onChangeKm}
                            onChangeIntegrant={onChangeIntegrant}
                            onRemove={removeCompositionRow}
                            canRemove={compositionEditable}
                        />

                        {compositionEditable &&
                            <div
                                ref={addMenuRef}
                                className="relative w-fit border border-transparent rounded-lg transition-[border-color,border-width] duration-150 ease-out"
                                data-required-field="true"
                                data-required-has-value={compositionRows.length > 0 ? "true" : "false"}
                                data-required-focus="true"
                                tabIndex={-1}
                                aria-label="Composições"
                            >
                                <Button onClick={() => setShowAddMenu(prev => !prev)}><FaPlus/>Adicionar</Button>
                                {showAddMenu &&
                                    <div className="absolute z-9999 mt-1 w-44 rounded-md border border-[#ededed] bg-white shadow-lg">
                                        <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100" onClick={() => addCompositionRow(true)}>
                                            <FaCar/> Com veículo
                                        </button>
                                        <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100" onClick={() => addCompositionRow(false)}>
                                            <FaWalking/> A pé
                                        </button>
                                    </div>
                                }
                            </div>
                        }

                        <GarrisonAnimalList
                            rows={animalRows}
                            animalOptions={animalOptions}
                            onChange={changeAnimal}
                            onRemove={removeAnimal}
                            disabled={!compositionEditable}
                            canRemove={compositionEditable}
                        />

                        {compositionEditable &&
                            <div className="w-fit">
                                <Button onClick={addAnimalRow}><FaPlus/>Adicionar animal</Button>
                            </div>
                        }

                        <InputTextarea
                            id="initialObservation"
                            label="Observação inicial"
                            textAreaClass={"h-25!"}
                            value={state.initialObservation}
                            onChange={({target: {value}}) => setState(prev => ({...prev, initialObservation: value}))}
                        />

                        {showFinalObservation &&
                            <InputTextarea
                                id="finalObservation"
                                label="Observação final"
                                textAreaClass={"h-25!"}
                                value={state.finalObservation}
                                onChange={({target: {value}}) => setState(prev => ({...prev, finalObservation: value}))}
                            />
                        }
                    </Card.Body>
                </Card>

                {showMaterialCaution &&
                    <Card>
                        <Card.Header className="flex items-center gap-2">
                            <Card.Title>Materiais em cautela</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <GarrisonMaterialCautionTable rows={materialCaution}/>
                        </Card.Body>
                    </Card>
                }

                {showDispatchCard &&
                    <Card>
                        <Card.Header className="flex items-center gap-2">
                            <Card.Title>Despachos / Atividades</Card.Title>
                            {showDispatchActions &&
                                <>
                                    <Button className="py-0! px-3! h-6!" title="Adicionar despacho" onClick={addDispatch}><FaPlus/></Button>
                                    <Button className="py-0! px-3! h-6!" title="Atualizar" onClick={refreshDispatch}><FaSyncAlt/></Button>
                                </>
                            }
                        </Card.Header>
                        <Card.Body>
                            <GarrisonDispatchTable rows={dispatchRows} boName={meta.boName}/>
                        </Card.Body>
                    </Card>
                }

                {panicRows.length > 0 &&
                    <Card>
                        <Card.Header>
                            <Card.Title>Pânico ativado</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <GarrisonPanicTable rows={panicRows}/>
                        </Card.Body>
                    </Card>
                }

                {renderInspectionCard && inspectionVisible &&
                    <Card>
                        <Card.Header>
                            <Card.Title>Vistoria de posto fixo</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <GarrisonFixedLocationInspection rows={inspectionRows} onChangeAnswer={onChangeAnswer}/>
                        </Card.Body>
                    </Card>
                }

                {showScheduleCard &&
                    <Card>
                        <Card.Header className="flex items-center gap-2">
                            <Card.Title>Atividades programadas</Card.Title>
                            {!state.finalized &&
                                <Button className="h-7.5! w-7.5 !p-0" title="Adicionar atividade programada" onClick={() => setShowScheduleModal(true)}><FaPlus/></Button>
                            }
                        </Card.Header>
                        <Card.Body className="flex flex-col gap-5">
                            <GarrisonScheduleList schedules={loadedSchedules} showDetails/>
                            <GarrisonScheduleList schedules={addedSchedules} onRemove={removeSchedule}/>

                            {((justifySchedules && state.finalized) || !!state.schedulesJustification) &&
                                <InputTextarea
                                    id="schedulesJustification"
                                    textAreaClass={`${REQUIRED_BG} h-25!`}
                                    labelClass={REQUIRED_LABEl_BG}
                                    label="Justificativa de atividades não realizadas *"
                                    value={state.schedulesJustification}
                                    onChange={({target: {value}}) => setState(prev => ({...prev, schedulesJustification: value}))}
                                />
                            }
                        </Card.Body>
                    </Card>
                }

                <Card>
                    <Card.Body className="flex gap-2 justify-end">
                        {showSaveCopy &&
                            <Button target="primary-outline" onClick={() => persist({addCopy: true})} actionFeedbackType='save'>Salvar e copiar</Button>
                        }
                        <Button target="primary-outline" onClick={() => persist({addAfter: true})} actionFeedbackType='save'>Salvar e adicionar novo</Button>
                        <Button onClick={() => persist()} actionFeedbackType='save'>Salvar</Button>
                    </Card.Body>
                </Card>
            </Template.Body>

            <GarrisonScheduleModal
                open={showScheduleModal}
                setOpen={setShowScheduleModal}
                garrisonId={mode === "edit" ? id : null}
                groups={groups}
                onConfirm={onConfirmSchedules}
            />
        </Template>
    );
};

export default GarrisonForm;